import { useEffect, useRef } from 'react';
import { useLocation, useNavigate, useRoutes } from 'react-router-dom';
import { publicRoutes } from '@/routes/public';
import { privateRoutes } from '@/routes/private';

// ログアウトメッセージの型定義
interface FrontchannelLogoutMessage {
  type: 'frontchannel_logout';
  redirectTo?: string;
  timestamp?: number;
  messageId: string;
}

// ACK メッセージの型定義（送信用）
interface FrontchannelLogoutAck {
  type: 'frontchannel_logout_ack';
  messageId: string;
}

// メッセージイベントの型ガード
const isFrontchannelLogoutMessage = (
  message: unknown
): message is FrontchannelLogoutMessage => {
  return (
    typeof message === 'object' &&
    message !== null &&
    'type' in message &&
    (message as FrontchannelLogoutMessage).type === 'frontchannel_logout' &&
    'messageId' in message &&
    typeof (message as FrontchannelLogoutMessage).messageId === 'string'
  );
};

export const AppRoutes = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const element = useRoutes([...publicRoutes, ...privateRoutes]);

  // 同一 messageId の重複処理を防止
  const handledIdsRef = useRef<Set<string>>(new Set());

  useEffect(() => {
    // 先に宣言（ハンドラ内で参照するため）
    let broadcastChannel: BroadcastChannel | null = null;

    // ACK 送信用ユーティリティ
    const sendAck = (
      ack: FrontchannelLogoutAck,
      targetOrigin?: string,
      targetWindow?: WindowProxy | null,
      bc?: BroadcastChannel | null
    ) => {
      try {
        targetWindow?.postMessage(ack, targetOrigin ?? '*');
      } catch {
        // Ignore parent window errors when posting messages
      }

      try {
        bc?.postMessage(ack);
      } catch {
        // Ignore broadcast channel errors when posting messages
      }

      try {
        localStorage.setItem('frontchannel_logout_ack', JSON.stringify(ack));
        setTimeout(() => {
          try {
            localStorage.removeItem('frontchannel_logout_ack');
          } catch {
            // Ignore localStorage errors when removing items
          }
        }, 100);
      } catch {
        // Ignore localStorage errors when setting items
      }
    };

    const handleLogout = (msg: FrontchannelLogoutMessage) => {
      const { redirectTo, timestamp, messageId } = msg;

      if (handledIdsRef.current.has(messageId)) return;
      handledIdsRef.current.add(messageId);

      const now = Date.now();
      const timeDiff = timestamp ? now - timestamp : 0;

      if (timestamp && timeDiff < 5 * 60 * 1000) {
        const targetPath = redirectTo || '/auth/login';
        const currentSearch = location.search;
        const finalPath = currentSearch
          ? `${targetPath}${currentSearch}`
          : targetPath;

        navigate(finalPath, { replace: true });
      }
    };

    const handlePostMessage = (event: MessageEvent) => {
      if (event.origin !== window.location.origin) return;
      if (isFrontchannelLogoutMessage(event.data)) {
        const ack: FrontchannelLogoutAck = {
          type: 'frontchannel_logout_ack',
          messageId: event.data.messageId,
        };
        sendAck(
          ack,
          event.origin,
          event.source as WindowProxy | null,
          broadcastChannel
        );
        handleLogout(event.data);
      }
    };

    try {
      broadcastChannel = new BroadcastChannel('frontchannel-logout');
      broadcastChannel.onmessage = (event) => {
        if (isFrontchannelLogoutMessage(event.data)) {
          const ack: FrontchannelLogoutAck = {
            type: 'frontchannel_logout_ack',
            messageId: event.data.messageId,
          };
          sendAck(ack, undefined, null, broadcastChannel);
          handleLogout(event.data);
        }
      };
    } catch (error) {
      console.error('BroadcastChannel not supported:', error);
    }

    const handleStorageEvent = (event: StorageEvent) => {
      if (event.key === 'frontchannel_logout_trigger' && event.newValue) {
        try {
          const message = JSON.parse(event.newValue);
          if (isFrontchannelLogoutMessage(message)) {
            const ack: FrontchannelLogoutAck = {
              type: 'frontchannel_logout_ack',
              messageId: message.messageId,
            };
            sendAck(ack, undefined, null, broadcastChannel);
            handleLogout(message);
          }
        } catch (error) {
          console.error('Failed to parse storage event:', error);
        }
      }
    };

    window.addEventListener('message', handlePostMessage);
    window.addEventListener('storage', handleStorageEvent);

    return () => {
      window.removeEventListener('message', handlePostMessage);
      window.removeEventListener('storage', handleStorageEvent);
      if (broadcastChannel) {
        broadcastChannel.close();
      }
    };
  }, [navigate, location.search]);

  return <>{element}</>;
};
